import React from 'react';
import styled from '@emotion/styled';
import {
  palette,
  status,
  gradient,
  spacing,
  radius,
  shadow,
  typography,
  transition,
  duration,
  easing,
  layout,
  zIndex,
} from '../../../../style-utils/theme';
import { down, touch, up } from '../../../../style-utils/media-queries';
import { fadeIn, fadeInUp, scaleIn } from '../../../../style-utils/motion';
import { Collapse, InlineMessage, Shake, m } from '../../../../motion';

/* Password reveal control — a real keyboard-operable 44px button inside the
   field, instead of a clickable icon with no semantics. */
export const PasswordVisibilityButton = styled.button`
  position: absolute;
  right: ${spacing.half};
  bottom: ${spacing.half};
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${layout.touchTarget};
  height: ${layout.touchTarget};
  padding: 0;
  border: none;
  border-radius: ${radius.xs};
  background: transparent;
  color: ${palette.slate};
  cursor: pointer;
  transition: ${transition.colors};

  &:hover {
    background: ${palette.irisTintSoft};
    color: ${palette.lagoon};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;

export const ModeTabs = styled.div`
  display: inline-flex;
  gap: ${spacing.half};
  margin: 0 ${spacing.sm} ${spacing.sm};
  padding: ${spacing.half};
  border-radius: ${radius.pill};
  background: ${palette.surfaceMuted};
`;

export const ModeTab = styled.button<{ $active?: boolean }>`
  height: ${layout.compactControlHeight};
  /* Coarse pointer: keep the compact look, meet the 44px floor. A desk tablet
     is this product's primary device, so a 32px control here is the common
     case and not the edge one. NO BACKTICKS IN THIS COMMENT - it lives inside a
     styled template literal. */
  ${touch} {
    height: auto;
    min-height: ${layout.touchTarget};
  }

  padding: 0 ${spacing.sm};
  border: none;
  border-radius: ${radius.pill};
  background: ${({ $active }) => ($active ? palette.lagoon : 'transparent')};
  color: ${({ $active }) => ($active ? palette.white : palette.slate)};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  cursor: pointer;
  transition: ${transition.colors};

  &:hover {
    background: ${({ $active }) =>
      $active ? palette.irisDark : palette.irisTintSoft};
    color: ${({ $active }) => ($active ? palette.white : palette.ink)};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;

export const InviteModePanel = styled.div`
  max-width: 560px;
  padding: 0 ${spacing.sm};
`;

export const PropertySelectWrapper = styled.div`
  position: relative;
`;

export const PropertyCheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  margin-bottom: ${spacing.half};
`;

/* ─────────────────────────  Shell  ───────────────────────── */

export const DashboardContent = styled.div<{ isAddNewUser?: boolean }>`
  min-height: 100%;
  width: 100%;
  background: ${({ isAddNewUser }) =>
    isAddNewUser ? palette.surface : 'transparent'};
  border-radius: ${radius.md};
  animation: ${fadeIn} ${duration.base} ${easing.standard} both;
`;

export const UsersListing = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: ${spacing.sm};
  padding: ${spacing.sm};
  background: ${palette.surface};
  border-bottom: 1px solid ${palette.line};
  border-radius: ${radius.md} ${radius.md} 0 0;

  /* It WRAPS from md up too. It was nowrap, and with the controls on the same
     row the title was the one child that could give way - measured at 1280 as
     "Use / rs", 37px wide. The controls are now one ListingControls group that
     drops under the title instead (dashboard.styled.tsx says why), and the
     title never shrinks. */
  ${up('md')} {
    padding: ${spacing.md};
    gap: ${spacing.md};

    & > *:first-child {
      flex: 0 0 auto;
    }
  }
`;

export const Title = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  margin-bottom: ${spacing.xs};
`;

export const NewUserButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing.xs};
  min-height: ${layout.touchTarget};
  padding: 0 ${spacing.md};
  border: none;
  cursor: pointer;
  border-radius: ${radius.sm};
  background: ${gradient.primary};
  color: ${palette.white};
  box-shadow: ${shadow.brand};
  font-family: ${typography.ui};
  font-size: 14px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.snug};
  white-space: nowrap;
  transition: ${transition.colors}, transform ${duration.fast} ${easing.spring};

  &:hover {
    background: ${gradient.primaryHover};
    box-shadow: ${shadow.brandHover};
    transform: translateY(-1px);
  }
  &:active {
    transform: scale(0.97);
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.brand}, ${shadow.focus};
  }

  ${up('lg')} {
    min-height: ${layout.controlHeight};
  }
`;

/* RESTORE (QA-09). A SECONDARY control, deliberately — undoing a delete is
   ordinary administration, not the screen's primary act, and giving it the
   brand gradient that "Add new user" wears would make the deleted-people view
   look like the place to start. Same touch target as every other row control
   (R-18), so it is reachable on a phone. */
export const RestoreButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: ${layout.touchTarget};
  padding: 0 ${spacing.md};
  cursor: pointer;
  border: 1px solid ${palette.line};
  border-radius: ${radius.sm};
  background: ${palette.white};
  color: ${palette.ink};
  font-family: ${typography.ui};
  font-size: 14px;
  font-weight: ${typography.weight.semibold};
  white-space: nowrap;
  transition: ${transition.colors};

  &:hover:not(:disabled) {
    background: ${palette.porcelain};
  }
  &:disabled {
    cursor: default;
    color: ${palette.slate};
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;

/* ─────────────────────────  Layout primitives  ───────────────────────── */

export const Column = styled.div<{ isCentered?: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: ${spacing.sm};

  ${up('md')} {
    padding: ${spacing.md};
  }
`;

export const Row = styled.div<{ isCentered?: boolean }>`
  display: flex;
  flex: 1;
  ${({ isCentered }) =>
    isCentered &&
    `
    align-items: center;
    justify-content: center;
  `}
`;

/* Card visual for a table row (and its header variant). */
export const StyledFlex = styled.div<{ isHeaderRow?: boolean }>`
  display: flex;
  width: 100%;
  box-sizing: border-box;
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  box-shadow: ${shadow.sm};
  overflow: visible;
  transition: ${transition.colors};

  ${down('md')} {
    flex-direction: column;
    padding: ${spacing.sm};
  }

  ${({ isHeaderRow }) =>
    isHeaderRow
      ? `
        background: transparent;
        border: none;
        box-shadow: none;
      `
      : `
        &:hover {
          background: ${palette.irisTintSoft};
          border-color: ${palette.lineStrong};
          box-shadow: ${shadow.md};
        }
      `}
`;

export const StyledFlexRow = styled(Row)<{ isHeaderRow?: boolean }>`
  flex: 0 0 auto;
  width: 100%;
  border-radius: ${radius.md};
  margin-bottom: ${spacing.xs};
  animation: ${fadeInUp} ${duration.base} ${easing.entrance} backwards;

  &:first-of-type {
    margin-top: ${spacing.xs};
  }

  ${({ isHeaderRow }) =>
    isHeaderRow &&
    `
      animation: none;
      margin-bottom: ${spacing.xs};
      ${down('md')} {
        display: none;
      }
    `}
`;

/* total desktop cell widths must sum to 100 */
export const ItemContainer = styled.div<{
  width?: number;
  isHeader?: boolean | undefined;
  isImage?: boolean | undefined;
  isActions?: boolean;
  isColumnHeader?: boolean;
}>`
  box-sizing: border-box;
  width: ${({ width }) => (width ? `${width}%` : '100%')};
  min-width: 0;
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  overflow: visible;
  padding: ${spacing.sm};
  background: transparent;

  ${up('md')} {
    height: ${({ isHeader }) => (isHeader ? '64px' : 'auto')};
    justify-content: ${({ isImage }) => (isImage ? 'center' : 'flex-start')};

    &:nth-child(6) {
      justify-content: flex-end;
    }
  }

  ${({ isColumnHeader }) =>
    isColumnHeader &&
    `
      text-transform: uppercase;
    `}

  ${down('md')} {
    width: 100%;
    padding: ${spacing.xs} 0;
    border-bottom: 1px solid ${palette.line};

    &:last-child {
      border-bottom: none;
    }

    &[data-label] {
      justify-content: space-between;

      &::before {
        content: attr(data-label);
        color: ${palette.slate};
        font-family: ${typography.ui};
        font-size: 12px;
        font-weight: ${typography.weight.semibold};
        letter-spacing: ${typography.tracking.caps};
        text-transform: uppercase;
        flex: 0 0 auto;
      }
    }
  }
`;

export const Button = styled.button<{
  backgroundColor: string;
  textColor: string;
}>`
  min-height: ${layout.controlHeight};
  padding: 0 ${spacing.sm};
  background-color: ${({ backgroundColor }) => backgroundColor};
  color: ${({ textColor }) => textColor};
  font-family: ${typography.ui};
  font-size: 14px;
  font-weight: ${typography.weight.semibold};
  cursor: pointer;
  border: none;
  border-radius: ${radius.sm};
  transition: ${transition.colors}, transform ${duration.fast} ${easing.spring};

  &:active {
    transform: scale(0.97);
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;

/* ─────────────────────────  Avatar  ───────────────────────── */

export const Avatar = styled.div`
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  border-radius: ${radius.circle};
  padding: ${spacing.half};
  background: linear-gradient(
    135deg,
    ${palette.iris} 0%,
    ${palette.irisDark} 100%
  );
  box-shadow: ${shadow.xs};
  display: grid;
  place-items: center;
`;

export const AvatarFallback = styled.div`
  width: 100%;
  height: 100%;
  border-radius: ${radius.circle};
  background: ${palette.irisTint};
  color: ${palette.irisDark};
  display: grid;
  place-items: center;
  overflow: hidden;
  font-family: ${typography.ui};
  font-size: 14px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.snug};
`;

export const AvatarImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: ${radius.circle};
`;

/* ─────────────────────────  Empty state  ───────────────────────── */

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${spacing.sm};
  text-align: center;
  padding: ${spacing.xxl} ${spacing.sm};
`;

export const EmptyStateIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: ${radius.circle};
  background: ${palette.irisTint};
  color: ${palette.lagoon};
  display: grid;
  place-items: center;
  font-size: ${typography.size.xxl}px;
`;

/* ─────────────────────────  Inputs  ───────────────────────── */

export const StyledInput = styled.input`
  box-sizing: border-box;
  width: 100%;
  height: ${layout.fieldHeight};
  padding: 0 ${spacing.sm};
  display: flex;
  align-items: center;
  border-radius: ${radius.xs};
  border: 1px solid ${palette.line};
  background: ${palette.surface};
  box-shadow: ${shadow.xs};
  color: ${palette.ink};
  font-family: ${typography.ui};
  font-size: 16px;
  letter-spacing: ${typography.tracking.snug};
  transition: ${transition.colors};

  ::placeholder {
    color: ${palette.slateLight};
  }

  &:hover {
    border-color: ${palette.lineStrong};
  }

  &:focus {
    outline: none;
    border-color: ${palette.lagoon};
    box-shadow: ${shadow.focus};
    background: ${palette.surface};
  }
`;

export const AddNewUserFormContainer = styled.div`
  box-sizing: border-box;
  width: 100%;
  margin: auto;
  display: flex;
  flex-direction: row;
  justify-content: center;
  padding: ${spacing.sm};
  overflow: auto;

  ${up('md')} {
    padding: ${spacing.md} ${spacing.sm};
  }
`;

export const AddNewUserForm = styled.form`
  width: 100%;
  max-width: 480px;
  position: relative;
`;

export const InputContainer = styled.div<{
  isImageUpload?: boolean;
  isPasswordInput?: boolean;
  isRoleField?: boolean;
}>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  margin-top: ${spacing.sm};
  border-radius: ${radius.xs};

  ${({ isPasswordInput }) => isPasswordInput && 'position: relative;'}

  ${({ isRoleField }) => isRoleField && 'position: relative;'}

  ${({ isImageUpload }) =>
    isImageUpload &&
    `
      align-items: center;
      justify-content: center;
      height: 88px;
      position: relative;
      border: 1px dashed ${palette.lineStrong};
      background: ${palette.surfaceMuted};
      transition: ${transition.colors};

      &:hover {
        border-color: ${palette.lagoon};
        background: ${palette.irisTintSoft};
      }
    `}

  input[type='file'] {
    height: 100%;
    width: 100%;
    z-index: 2;
    position: absolute;
    opacity: 0;
    cursor: pointer;
  }
`;

export const ImageUploadContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  position: relative;
`;

export const Label = styled.label<{
  isImageUpload?: boolean;
}>`
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.medium};
  letter-spacing: ${typography.tracking.snug};
  margin-bottom: ${({ isImageUpload }) => (isImageUpload ? '0' : spacing.xs)};
  margin-left: ${({ isImageUpload }) => (isImageUpload ? spacing.sm : '0')};
  color: ${palette.inkSoft};
`;

export const ErrorLabel = styled.p`
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.medium};
  letter-spacing: ${typography.tracking.snug};
  color: ${status.alert.fg};
  display: block;
  margin-top: ${spacing.xs};
  margin-bottom: 0;
`;

interface FieldErrorProps {
  /** Render (and unfold) the message when true; fold it away when false. */
  show?: boolean;
  /** Bump on every refused submit so the refusal replays on a second try. */
  attempt?: number;
  id?: string;
  children: React.ReactNode;
}

/**
 * A field's validation line: it UNFOLDS under its field (InlineMessage keeps
 * the space animated so nothing below jumps) and SHAKES once as it arrives —
 * the refusal the old keyframe played, now re-armed per attempt so pressing
 * Save twice on the same mistake is answered twice. role="alert" on the
 * wrapper announces it.
 */
export const FieldError: React.FC<FieldErrorProps> = ({
  show = false,
  attempt = 0,
  id,
  children,
}) => (
  <InlineMessage
    id={id}
    role="alert"
    message={
      show ? (
        <Shake active attempt={attempt}>
          <ErrorLabel as="span">{children}</ErrorLabel>
        </Shake>
      ) : undefined
    }
  />
);

/* Wraps a form's fields so ONE `disabled` makes every control inside inert
   while a save is in flight. Reset to nothing: a fieldset defaults to a
   border, padding and a content min-width, all of which would resize the
   480px form. */
export const FormFieldset = styled.fieldset`
  box-sizing: border-box;
  display: block;
  width: 100%;
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
`;

export const StyledUserInput = styled.input<{
  isImageUpload?: boolean;
  isCountryCodeInput?: boolean;
  hasTrailingAction?: boolean;
}>`
  box-sizing: border-box;
  width: 100%;
  height: ${layout.fieldHeight};
  padding: ${({ hasTrailingAction }) =>
    hasTrailingAction
      ? `0 ${spacing.xxxl} 0 ${spacing.sm}`
      : `0 ${spacing.sm}`};
  display: flex;
  align-items: center;
  border-radius: ${radius.xs};
  border: 1px solid ${palette.line};
  background: ${palette.surface};
  box-shadow: ${shadow.xs};
  color: ${palette.ink};
  font-family: ${typography.ui};
  font-size: 16px;
  letter-spacing: ${typography.tracking.snug};
  transition: ${transition.colors};

  ::placeholder {
    color: ${palette.slateLight};
  }

  &:hover {
    border-color: ${palette.lineStrong};
  }

  &:focus {
    outline: none;
    border-color: ${palette.lagoon};
    box-shadow: ${shadow.focus};
    background: ${palette.surface};
  }

  ${({ isImageUpload }) =>
    isImageUpload &&
    `
      height: 100%;
      padding: 0;
      border: none;
      box-shadow: none;
      background: transparent;
    `}
`;

export const StyledSelectMenu = styled.select<{ isRoleField?: boolean }>`
  -webkit-appearance: none;
  appearance: none;
  box-sizing: border-box;
  width: 100%;
  height: ${layout.fieldHeight};
  padding: 0 ${spacing.md};
  border-radius: ${radius.xs};
  border: 1px solid ${palette.line};
  background: ${palette.surface};
  box-shadow: ${shadow.xs};
  color: ${palette.ink};
  font-family: ${typography.ui};
  font-size: 16px;
  cursor: pointer;
  transition: ${transition.colors};

  &:hover {
    border-color: ${palette.lineStrong};
  }

  &:focus {
    outline: none;
    border-color: ${palette.lagoon};
    box-shadow: ${shadow.focus};
  }

  ${({ isRoleField }) =>
    isRoleField &&
    `
      height: 56px;
      font-size: 18px;
      font-weight: ${typography.weight.semibold};
      text-align: center;
      text-transform: capitalize;
      border-color: ${palette.lineStrong};
      box-shadow: ${shadow.sm};
    `}
`;

export const CheckRingContainer = styled.div<{ isFilterChecker?: boolean }>`
  position: absolute;
  z-index: 1;
  margin-left: ${spacing.sm};
  margin-top: ${({ isFilterChecker }) =>
    isFilterChecker ? spacing.sm : spacing.xs};
`;

export const CountryCodeContainer = styled.div`
  display: flex;
  align-items: stretch;
  gap: ${spacing.xs};
`;

export const CountryCodeSelect = styled.select`
  box-sizing: border-box;
  height: ${layout.fieldHeight};
  padding: 0 ${spacing.xs};
  border-radius: ${radius.xs};
  border: 1px solid ${palette.line};
  background: ${palette.surface};
  box-shadow: ${shadow.xs};
  color: ${palette.ink};
  font-family: ${typography.ui};
  font-size: 14px;
  cursor: pointer;
  transition: ${transition.colors};

  &:hover {
    border-color: ${palette.lineStrong};
  }
  &:focus {
    outline: none;
    border-color: ${palette.lagoon};
    box-shadow: ${shadow.focus};
  }
`;

export const CountryCode = styled.div`
  font-size: 14px;
  margin-bottom: ${spacing.half};
  margin-right: ${spacing.xs};
`;

/* ─────────────────────────  Form footer buttons  ───────────────────────── */

export const ButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: ${spacing.sm};
  margin-top: ${spacing.lg};
  padding: 0 ${spacing.sm} ${spacing.md};

  ${up('md')} {
    padding: 0 ${spacing.md} ${spacing.md};
  }
`;

export const CancelButton = styled.button`
  display: inline-flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  min-height: ${layout.touchTarget};
  padding: 0 ${spacing.md};
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.sm};
  color: ${palette.slate};
  cursor: pointer;
  transition: ${transition.colors}, transform ${duration.fast} ${easing.spring};

  &:hover {
    background: ${palette.irisTintSoft};
    border-color: ${palette.lineStrong};
    color: ${palette.inkSoft};
  }
  &:active {
    transform: scale(0.97);
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

  &:disabled {
    cursor: not-allowed;
    color: ${palette.slateLight};
    background: ${palette.surfaceMuted};
  }
`;

export const SaveButton = styled.button`
  display: inline-flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: ${spacing.xs};
  min-height: ${layout.touchTarget};
  padding: 0 ${spacing.md};
  background: ${gradient.primary};
  border: none;
  border-radius: ${radius.sm};
  color: ${palette.white};
  cursor: pointer;
  box-shadow: ${shadow.brand};
  font-family: ${typography.ui};
  font-weight: ${typography.weight.semibold};
  transition: ${transition.colors}, transform ${duration.fast} ${easing.spring};

  svg {
    transition: transform ${duration.fast} ${easing.spring};
  }

  &:hover {
    background: ${gradient.primaryHover};
    box-shadow: ${shadow.brandHover};
    transform: translateY(-1px);
  }
  &:hover svg {
    transform: translateX(2px);
  }
  &:active {
    transform: scale(0.97);
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.brand}, ${shadow.focus};
  }

  &:disabled {
    cursor: not-allowed;
    background: ${palette.surfaceMuted};
    color: ${palette.slateLight};
    box-shadow: none;
    transform: none;
  }
`;

export const ButtonTitle = styled.span`
  margin: 0;
  font-family: ${typography.ui};
  font-size: 14px;
  font-style: normal;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.snug};
`;

/* ─────────────────────────  Row action popover  ───────────────────────── */

export const ActionModalBackground = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: transparent;
  z-index: ${zIndex.dropdown};
`;

/* The row action menu — a motion root that grows from the trigger's corner
   (overlay kind 'popover', origin top right) and shrinks back inside a
   Presence when dismissed. The transparent ActionModalBackground above is the
   click-catcher and needs no motion of its own. */
export const ActionModal = m.div`
  position: absolute;
  right: ${spacing.sm};
  min-width: 184px;
  border-radius: ${radius.md};
  padding: ${spacing.xs};
  gap: ${spacing.xs};
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  box-shadow: ${shadow.md};
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  transform-origin: top right;
  z-index: ${zIndex.dropdown};
`;

export const RowActionTrigger = styled.button`
  -webkit-appearance: none;
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${layout.touchTarget};
  height: ${layout.touchTarget};
  padding: 0;
  border: none;
  border-radius: ${radius.circle};
  background: transparent;
  color: ${palette.slate};
  cursor: pointer;

  &:hover {
    background: ${palette.irisTintSoft};
    color: ${palette.lagoon};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;

export const ButtonContainer = styled.button<{ isDelete?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: ${layout.buttonHeight};
  min-width: 80px;
  padding: 0 ${spacing.sm};
  border: none;
  border-radius: ${radius.sm};
  color: ${palette.white};
  cursor: pointer;
  background: ${({ isDelete }) =>
    isDelete ? palette.alertStrong : palette.lagoon};
  box-shadow: ${({ isDelete }) => (isDelete ? shadow.md : shadow.brand)};
  transition: ${transition.colors}, transform ${duration.fast} ${easing.spring};

  &:hover {
    transform: translateY(-1px);
    background: ${({ isDelete }) =>
      isDelete ? palette.alertDeep : palette.tealDeep};
  }
  &:active {
    transform: scale(0.97);
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${({ isDelete }) =>
      isDelete ? shadow.focusError : shadow.focus};
  }
`;

export const StyledImage = styled.img<{ isAddNewUserForm?: boolean }>`
  width: 24px;
  height: 24px;
  display: inline-block;
  border-radius: ${radius.xs};
  margin-right: ${spacing.xs};

  width: ${({ isAddNewUserForm }) => isAddNewUserForm && '40px'};
  height: ${({ isAddNewUserForm }) => isAddNewUserForm && '40px'};
`;

/* ─────────────────────────  Role status pill  ───────────────────────── */

export const RoleDiv = styled.div<{ isManager: boolean; isAdmin: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.xs};
  min-height: 24px;
  padding: ${spacing.half} ${spacing.sm};
  border-radius: ${radius.pill};
  font-family: ${typography.ui};
  font-size: 12px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.snug};
  white-space: nowrap;
  background: ${({ isAdmin }) =>
    isAdmin ? status.brand.bg : status.neutral.bg};
  color: ${({ isAdmin }) => (isAdmin ? status.brand.fg : status.neutral.fg)};

  &::before {
    content: '';
    width: ${spacing.xs};
    height: ${spacing.xs};
    border-radius: ${radius.circle};
    flex: 0 0 auto;
    background: ${({ isAdmin }) =>
      isAdmin ? status.brand.dot : status.neutral.dot};
  }
`;

/* ─────────────────────────  Destructive delete modal  ─────────────────────────
 *
 * Currently UNUSED — user deletion goes through <ConfirmDialog>, which portals
 * itself. Kept only as the styling for a bespoke destructive sheet. If it is ever
 * mounted again it MUST be wrapped in <ModalPortal>: rendered inline it inherits
 * the screen's entrance animation as a containing block, so `position: fixed`
 * covers the screen box rather than the viewport and the sticky header stays
 * bright above the scrim (see components/modal/modal-portal.tsx). */

export const DeleteModalBackground = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: ${palette.overlay};
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${spacing.sm};
  z-index: ${zIndex.modal};
  animation: ${fadeIn} ${duration.fast} ${easing.standard} both;
`;

export const DeleteModal = styled.div`
  box-sizing: border-box;
  background: ${palette.surface};
  width: 100%;
  max-width: 400px;
  padding: ${spacing.lg} ${spacing.md} ${spacing.md};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  gap: ${spacing.xs};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  box-shadow: ${shadow.lg};
  z-index: ${zIndex.modal};
  animation: ${scaleIn} ${duration.base} ${easing.spring} both;
`;

export const ModalIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: ${radius.circle};
  background: ${palette.alertSoft};
  color: ${status.alert.fg};
  display: grid;
  place-items: center;
  font-size: 26px;
  margin-bottom: ${spacing.xs};
`;

export const CancelConfirmButton = styled.button<{
  isConfirm?: boolean;
  isDestructive?: boolean;
}>`
  display: flex;
  height: ${layout.touchTarget};
  justify-content: center;
  align-items: center;
  align-self: stretch;
  border-radius: ${radius.sm};
  cursor: pointer;
  font-family: ${typography.ui};
  font-size: 14px;
  font-weight: ${typography.weight.semibold};
  transition: ${transition.colors}, transform ${duration.fast} ${easing.spring};

  background: ${({ isConfirm, isDestructive }) =>
    isDestructive ? palette.alert : isConfirm ? palette.iris : palette.surface};
  color: ${({ isConfirm, isDestructive }) =>
    isConfirm || isDestructive ? palette.white : palette.slate};
  border: 1px solid
    ${({ isConfirm, isDestructive }) =>
      isConfirm || isDestructive ? 'transparent' : palette.line};
  box-shadow: ${({ isConfirm, isDestructive }) =>
    isDestructive ? shadow.md : isConfirm ? shadow.brand : 'none'};

  &:hover {
    transform: translateY(-1px);
    background: ${({ isConfirm, isDestructive }) =>
      isDestructive
        ? status.alert.fg
        : isConfirm
        ? palette.irisDark
        : palette.irisTintSoft};
  }
  &:active {
    transform: scale(0.98);
  }
  /* KEYBOARD GETS WHAT THE POINTER GETS. This control styled :hover and nothing
     for focus, so it acknowledged a mouse and ignored a keyboard. */
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

`;

/* ─────────────────────────  Property multi-select  ───────────────────────── */

export const StyledSelectCheckboxes = styled.button`
  -webkit-appearance: none;
  appearance: none;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  width: 100%;
  min-height: ${layout.fieldHeight};
  padding: 0 ${spacing.sm};
  border-radius: ${radius.xs};
  border: 1px solid ${palette.line};
  background: ${palette.surface};
  box-shadow: ${shadow.xs};
  color: ${palette.ink};
  font-family: ${typography.ui};
  font-size: 14px;
  text-align: left;
  cursor: pointer;
  transition: ${transition.colors};

  &:hover {
    border-color: ${palette.lineStrong};
  }
  /* KEYBOARD GETS WHAT THE POINTER GETS. This control styled :hover and nothing
     for focus, so it acknowledged a mouse and ignored a keyboard. */
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

`;

/* The properties picker's dropdown, UNFOLDING under its button.

   Two layers on purpose. The outer one is a Collapse (src/motion): it owns the
   position, the border and the shadow, and animates its height open and
   closed — the shadow lives out here because Collapse clips its overflow
   while it runs. The inner one is the scroller and the group. A Collapse
   wrapped around an absolutely positioned child would measure it as 0px tall
   and never open, which is why the positioning moved out here. */
export const CheckboxesDropdown = styled(Collapse)`
  box-sizing: border-box;
  position: absolute;
  top: calc(100% + ${spacing.xs});
  left: 0;
  width: 100%;
  z-index: ${zIndex.dropdown};
  border-radius: ${radius.sm};
  border: 1px solid ${palette.line};
  background: ${palette.surface};
  box-shadow: ${shadow.md};
`;

export const CheckboxesContainerInSelectMenu = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  padding: ${spacing.sm};
  width: 100%;
  max-height: 240px;
  overflow-y: auto;
  color: ${palette.ink};
  font-family: ${typography.ui};
  font-size: 14px;

  label {
    display: flex;
    align-items: center;
    gap: ${spacing.xs};
    padding: ${spacing.half} 0;
    cursor: pointer;
    border-radius: ${radius.xs};
    transition: ${transition.colors};
  }

  label:hover {
    color: ${palette.irisDark};
  }

  input[type='checkbox'] {
    width: 16px;
    height: 16px;
    accent-color: ${palette.lagoon};
    cursor: pointer;
  }
`;

export const ViewPropertiesButton = styled.button`
  -webkit-appearance: none;
  appearance: none;
  display: inline-flex;
  min-height: ${layout.touchTarget};
  width: 100%;
  padding: ${spacing.xs} ${spacing.sm};
  justify-content: space-between;
  align-items: center;
  gap: ${spacing.xs};
  border-radius: ${radius.sm};
  background: ${palette.irisTintSoft};
  border: 1px solid ${palette.line};
  color: ${palette.irisDark};
  cursor: pointer;
  transition: ${transition.colors};

  &:hover {
    background: ${palette.irisTint};
    border-color: ${palette.lineStrong};
  }

  ${down('md')} {
    width: auto;
    min-height: ${layout.touchTarget};
  }
  /* KEYBOARD GETS WHAT THE POINTER GETS. This control styled :hover and nothing
     for focus, so it acknowledged a mouse and ignored a keyboard. */
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

`;

/* The "View N properties" popover — a motion root (overlay kind 'popover',
   origin top left) inside a Presence, so it shrinks back when dismissed. */
export const PropertiesModal = m.div`
  box-sizing: border-box;
  position: absolute;
  min-height: 40px;
  height: fit-content;
  width: 200px;
  border-radius: ${radius.md};
  padding: ${spacing.xs};
  left: 34.5%;
  gap: ${spacing.half};
  background: ${palette.surface};
  box-shadow: ${shadow.md};
  display: flex;
  flex-direction: column;
  justify-content: center;
  border: 1px solid ${palette.line};
  z-index: ${zIndex.dropdown};
  transform-origin: top left;

  ${down('xl')} {
    width: 176px;
    left: 37.5%;
  }
`;

/* ── PEOPLE TYPE FILTER (WP-14 / BUG-010) ────────────────────────────────────
   The FIELD density, deliberately, not the compact bar density: this control
   sits beside the search box on a form-shaped row, so it matches StyledInput
   above token for token — same height, radius, border, shadow and 16px text.
   Anything shorter would read as a different class of control standing next to
   its twin.

   16px is not a style choice either. Under a coarse pointer iOS zooms the whole
   page when a focused control's text is under 16px, so a 13px select here would
   make the People screen jump on every tap on a phone. */
export const TypeFilterLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.half};
  flex: 0 0 auto;
  color: ${palette.slate};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  letter-spacing: ${typography.tracking.snug};
  white-space: nowrap;
`;

export const TypeFilterSelect = styled.select`
  box-sizing: border-box;
  height: ${layout.fieldHeight};
  min-height: ${layout.touchTarget};
  padding: 0 ${spacing.sm};
  border-radius: ${radius.xs};
  border: 1px solid ${palette.line};
  background: ${palette.surface};
  box-shadow: ${shadow.xs};
  color: ${palette.ink};
  font-family: ${typography.ui};
  font-size: 16px;
  letter-spacing: ${typography.tracking.snug};
  transition: ${transition.colors};
  cursor: pointer;

  &:hover {
    border-color: ${palette.lineStrong};
  }

  &:focus-visible {
    outline: none;
    border-color: ${palette.lagoon};
    box-shadow: ${shadow.focus};
  }
`;
